/**
 * Equipment catalog updates — the admin card that brings a plant's stored equipment
 * definitions up to the current catalog (lib/plant/catalogUpgrade).
 *
 * Equipment keeps the definition it was created with, so a parameter the catalog gains
 * reaches new equipment only, and an imported value has no slot to land in on the old
 * ones. The card previews exactly what an upgrade appends (the preview IS the dry run:
 * nothing is written until the admin confirms), then writes one entity at a time.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { isLiveApi, TWIN_ENTITIES_KEY } from "@/lib/api/queries";
import { twinEntities } from "@/lib/api/twin-entities";
import {
  applyUpgrades,
  listAllEntities,
  planRowUpgrade,
  summarise,
  type ApplyResult,
  type RowUpgrade,
} from "@/lib/plant/catalogUpgrade";

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function paramLabel(p: { name: string; unit?: string }) {
  return p.unit ? `${p.name} (${p.unit})` : p.name;
}

export interface CatalogUpgradeViewProps {
  plans: RowUpgrade[];
  results: ApplyResult[] | null;
  busy: boolean;
  canApply: boolean;
  onApply: () => void;
}

/** The preview, the button and the outcome. Pure: everything it shows comes from props. */
export function CatalogUpgradeView({ plans, results, busy, canApply, onApply }: CatalogUpgradeViewProps) {
  const s = summarise(plans);
  const upgrades = plans.filter((p): p is Extract<RowUpgrade, { kind: "upgrade" }> => p.kind === "upgrade");
  const skipped = plans.filter((p): p is Extract<RowUpgrade, { kind: "skipped" }> => p.kind === "skipped");
  const differences = plans.flatMap((p) =>
    p.kind === "skipped" ? [] : p.plan.differences.map((d) => ({ entity: p.name, ...d })),
  );

  return (
    <div className="space-y-4 text-sm">
      {s.toUpgrade === 0 ? (
        <p className="text-on-surface">
          Every equipment matches the catalog.{" "}
          <span className="text-muted-foreground">
            {plural(s.current, "equipment is", "equipment are")} current
            {s.skipped ? `; ${s.skipped} not updated (see below)` : ""}.
          </span>
        </p>
      ) : (
        <>
          <p className="text-on-surface">
            <strong>
              {s.toUpgrade} of {s.entities} equipment
            </strong>{" "}
            {s.toUpgrade === 1 ? "is" : "are"} missing {plural(s.parameters, "catalog parameter")}
            {s.semantics ? `, and ${plural(s.semantics, "existing parameter")} gain their class or setpoint pairing` : ""}.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3 font-medium">Equipment</th>
                  <th className="py-1 pr-3 font-medium">Template</th>
                  <th className="py-1 font-medium">Adds</th>
                </tr>
              </thead>
              <tbody>
                {upgrades.map((u) => (
                  <tr key={u.id} className="border-t border-border align-top">
                    <td className="py-1.5 pr-3 font-medium text-on-surface">{u.name}</td>
                    <td className="py-1.5 pr-3 font-mono text-muted-foreground">{u.templateId}</td>
                    <td className="py-1.5">
                      {u.plan.add.map(paramLabel).join(", ") || "—"}
                      {u.plan.semantics.length > 0 && (
                        <span className="text-muted-foreground">
                          {" "}
                          · class or pairing for {u.plan.semantics.map((x) => x.name).join(", ")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted-foreground">
            Adds definitions only. No values or readings are written, and no existing parameter is renamed, removed
            or changed.
          </p>
          <Button onClick={onApply} disabled={!canApply || busy}>
            {busy
              ? "Adding…"
              : `Add ${plural(s.parameters, "parameter")} to ${plural(s.toUpgrade, "equipment", "equipment")}`}
          </Button>
        </>
      )}

      {differences.length > 0 && (
        <details>
          <summary className="cursor-pointer text-muted-foreground">
            {differences.length === 1 ? "1 stored parameter differs" : `${differences.length} stored parameters differ`}{" "}
            from the catalog. Left unchanged: changing a unit or data kind would change what the stored readings mean.
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {differences.map((d) => (
              <li key={`${d.entity}.${d.name}.${d.field}`}>
                {d.entity} · <span className="font-mono">{d.name}</span> {d.field}: stored {d.stored ?? "—"}, catalog{" "}
                {d.catalog ?? "—"}
              </li>
            ))}
          </ul>
        </details>
      )}

      {skipped.length > 0 && (
        <details>
          <summary className="cursor-pointer text-muted-foreground">
            {skipped.length} equipment not updated
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {skipped.map((k) => (
              <li key={k.id}>
                {k.name}: {k.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      {results && (
        <ul className="space-y-1 text-xs" aria-label="Upgrade results">
          {results.map((r) => (
            <li key={r.id} className="flex items-center gap-2">
              <Badge variant={r.status === "written" ? "success" : r.status === "failed" ? "danger" : "secondary"}>
                {r.status === "failed" ? `failed: ${r.error ?? "unknown error"}` : r.status}
              </Badge>
              <span>{r.name}</span>
              {r.status === "written" && r.added ? (
                <span className="text-muted-foreground">+{plural(r.added, "parameter")}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The card: reads every entity of the active org, previews the upgrade, and applies it on
 *  confirmation. Mounted for org admins on a live API only. */
export function CatalogUpgradeCard() {
  const { activeOrgId, canAdmin } = useOrg();
  const toast = useToast();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ApplyResult[] | null>(null);

  const entities = useQuery({
    queryKey: ["catalog-upgrade", activeOrgId],
    queryFn: () => listAllEntities((p) => twinEntities.list(p)),
    enabled: Boolean(activeOrgId) && isLiveApi,
  });
  const plans = useMemo(() => (entities.data ?? []).map((r) => planRowUpgrade(r)), [entities.data]);
  const s = summarise(plans);

  const apply = async () => {
    setBusy(true);
    try {
      const res = await applyUpgrades(plans, { get: twinEntities.get, create: twinEntities.create });
      setResults(res);
      const failed = res.filter((r) => r.status === "failed").length;
      const written = res.filter((r) => r.status === "written").length;
      toast.push(
        failed ? "error" : "ok",
        failed
          ? `${plural(failed, "equipment", "equipment")} could not be updated; ${written} updated. See the list.`
          : `${plural(written, "equipment", "equipment")} updated.`,
      );
      await Promise.all([entities.refetch(), qc.invalidateQueries({ queryKey: TWIN_ENTITIES_KEY })]);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Equipment catalog updates</CardTitle>
        <CardDescription>
          Equipment keeps the definition it was created with. When the catalog gains a parameter, existing equipment
          needs it added before an import can put values in that slot.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {entities.isLoading ? (
          <p className="text-sm text-muted-foreground">Reading this plant's equipment…</p>
        ) : entities.isError ? (
          <p className="text-sm text-danger">Couldn't read this plant's equipment. Nothing was changed.</p>
        ) : (
          <CatalogUpgradeView
            plans={plans}
            results={results}
            busy={busy}
            canApply={canAdmin}
            onApply={() => setConfirming(true)}
          />
        )}
      </CardContent>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={apply}
        title="Add the missing catalog parameters?"
        description={`Appends ${plural(s.parameters, "parameter definition")} to ${plural(
          s.toUpgrade,
          "equipment",
          "equipment",
        )}. No values or readings are written, and existing parameters stay as they are.`}
        confirmLabel="Add parameters"
      />
    </Card>
  );
}
